import {HiOutlineTrash, HiX} from "react-icons/hi";
import React, {useEffect, useState} from "react";
import {deleteChat, resetChatIdForDelete, setIsNewChat, setLastActiveChatId} from "../../redux/slices/chat";
import {useDispatch, useSelector} from "react-redux";
import axios from '../../utils/axiosConfig';
import toast from 'react-hot-toast';

interface DeleteChatModalProps {
  onCloseModal: () => void;
}

const DeleteChatModal: React.FC<DeleteChatModalProps> = ({ onCloseModal }) => {
  const dispatch = useDispatch();
  const [isDeleting, setIsDeleting] = useState(false);

  const {
    chatIdForDelete,
    lastActiveChatId,
  } = useSelector((state: any) => state.chatMessages);

  const smartDeleteChat = async (chatId: any, isDeletingActiveChat: boolean) => {
    setIsDeleting(true);
    try {
      const response = await axios.delete(`/chat/${chatId}`);
      toast.success(response.data.message);
      dispatch(deleteChat(chatId));

      if (isDeletingActiveChat) {
        dispatch(setLastActiveChatId(null));
        dispatch(setIsNewChat(true));
      }
    } catch (error) {
      console.error('Failed to delete chat:', error);
      toast.error('Failed to delete chat');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (chatIdForDelete) {
   
      const isDeletingActiveChat = chatIdForDelete === lastActiveChatId;
      dispatch(resetChatIdForDelete());
      onCloseModal();
      await smartDeleteChat(chatIdForDelete, isDeletingActiveChat);
    }
  };

  const handleCancel = () => {
    if (isDeleting) return;

    dispatch(resetChatIdForDelete());
    onCloseModal();
  };

  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !isDeleting) {
        handleCancel();
      }
    };

    document.addEventListener('keydown', handleEscapeKey);
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isDeleting]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  return (
    <div 
      className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm z-[99999] flex items-center justify-center p-4 animate-in fade-in duration-200"
      onClick={isDeleting ? undefined : handleCancel}
    >
      <div 
        className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden max-w-sm w-full animate-in zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
       
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-2.5">
            <div className="flex-shrink-0 w-8 h-8 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
              <HiOutlineTrash className="w-4 h-4 text-red-600 dark:text-red-400" />
            </div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">
              Delete Chat
            </h3>
          </div>
          <button
            onClick={handleCancel}
            disabled={isDeleting}
            className={`focus:outline-none focus:ring-2 focus:ring-gray-300 dark:focus:ring-gray-600 rounded-lg p-1 transition-colors duration-200 ${
              isDeleting
                ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
                : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'
            }`}
            aria-label="Close modal"
          >
            <HiX className="w-4 h-4" />
          </button>
        </div>

       
        <div className="p-4">
         
          <div className="flex items-start space-x-3 mb-3">
           
            <div className="flex-1 ">
              
              <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                This action cannot be undone. This will permanently delete the chat conversation and remove all message history.
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col-reverse sm:flex-row gap-2.5 sm:gap-2.5 sm:justify-end">
            <button
              onClick={handleCancel}
              disabled={isDeleting}
              className={`px-3 py-2 text-sm font-medium border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-300 dark:focus:ring-gray-600 transition-colors duration-200 ${
                isDeleting
                  ? 'text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-800 cursor-not-allowed'
                  : 'text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600'
              }`}
            >
              Cancel
            </button>
            <button
              onClick={handleDeleteConfirm}
              disabled={isDeleting}
              className={`px-3 py-2 text-sm font-medium text-white rounded-lg transition-colors duration-200 flex items-center justify-center space-x-1.5 focus:outline-none focus:ring-2 focus:ring-red-300 dark:focus:ring-red-800 ${
                isDeleting 
                  ? 'bg-red-400 cursor-not-allowed' 
                  : 'bg-red-600 hover:bg-red-700'
              }`}
            >
              {isDeleting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Deleting...</span>
                </>
              ) : (
                <>
                  <HiOutlineTrash className="w-4 h-4" />
                  <span>Delete Chat</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DeleteChatModal;